import { useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import MobileSidebar from '../Sidebar/MobileSidebar';
import { useAppBreadcrumbs } from '@/hooks/Header/useAppBreadcrumbs';
import { useViewportWidth } from '@/hooks/shared';
import { useAppFilters } from '@/hooks/shared/useAppFilters';
import { AppLogo } from '@/components/shared/display/Logo';
import { ASSETS_TYPE, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { useLocation } from 'react-router-dom';
import { BaseIconButton, TabType } from '@/modules';
import { ToggleTabs } from '@/components/uiKit/Tabs/ToggleTabs';
import { BaseBreadcrumb } from '@/components/uiKit/Breadcrumbs';
import { HeaderLogo } from '@/components/Header';
import { HamburgerIcon, IndustryWindowsIcon, TruckPlowIcon, LayerGroupIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';

// Sites page tabs - defined directly in code, not from registry
const SITES_PAGE_TABS: TabType[] = [
  {
    id: ASSETS_TYPE.ALL,
    title: 'All',
    tooltip: 'View all assets',
    icon: <Icon icon={LayerGroupIcon} />,
  },
  {
    id: ASSETS_TYPE.FIXED,
    title: 'Fixed',
    tooltip: 'View fixed assets',
    icon: <Icon icon={IndustryWindowsIcon} />,
  },
  {
    id: ASSETS_TYPE.MOBILE,
    title: 'Mobile',
    tooltip: 'View mobile assets',
    icon: <Icon icon={TruckPlowIcon} />,
  },
];

export const Header = () => {
  const { breadcrumbs } = useAppBreadcrumbs();
  const { isMobile, isTablet } = useViewportWidth();
  const filters = useAppFilters();
  const { pathname } = useLocation();
  const isSites = pathname.includes('sites');

  const tabs = useMemo(
    () =>
      SITES_PAGE_TABS.map((el: TabType) => {
        return {
          ...el,
          disabled:
            el.disabled ||
            (el.id === ASSETS_TYPE.ALL && !!filters.assetsPreview) ||
            (filters.assetsType !== el.id && !!filters.assetStudio) ||
            false,
        };
      }),
    [filters]
  );

  return (
    <div className="w-screen shadow-none sm:shadow-md flex items-center justify-between px-2 md:px-4 xl:px-6 fixed bg-white dark:bg-[#52606d] top-0 left-0 h-header z-[50] border-b border-[#e6e8ec] dark:border-[#2f3844]">
      <BaseIconButton
        classNames={{ button: 'text-default xl:hidden flex' }}
        icon={HamburgerIcon}
        variant={'ghost'}
        drawer={
          isMobile
            ? {
                element: <MobileSidebar />,
                direction: 'bottom',
              }
            : {
                className: 'bg-accent border-0 text-white',
                element: <MobileSidebar />,
                direction: 'left',
              }
        }
      />
      <div className="flex items-center gap-4 xl:w-full justify-center xl:justify-start">
        <HeaderLogo />
        <Separator orientation="vertical" className="h-5 w-0.5 hidden xl:block" />
        {!isTablet || filters.assetsPreview ? (
          <div className="flex gap-4 items-center w-full justify-start ">
            <BaseBreadcrumb breadcrumbs={breadcrumbs} className="hidden xl:flex items-center w-fit" />
          </div>
        ) : (
          <AppLogo size={''} />
        )}
      </div>
      <div className="flex gap-4 font-medium text-sm items-center ">
        {isSites && (
          <ToggleTabs
            tabs={tabs}
            defaultValue={filters.assetsType || SITES_PAGE_TABS[0]?.id || ASSETS_TYPE.ALL}
            paramAlias={FILTER_PARAMS.ASSETS_TYPE}
          />
        )}
      </div>
    </div>
  );
};
